import Pencil from 'svg/Pencil';
import { Button } from 'components/Button';
import DetailModalShell from 'components/Find/DetailModalShell';
import {
  PlayerAvailability,
  PlayerHeader,
  PlayerLookingFor,
  PlayerPreferredLocations,
} from 'components/Find/PlayerDetails';
import { DetailSection, PersonCard } from 'components/Find/detail';
import HostRequestsCard from './HostRequestsCard';
import type { HostPlayRequest } from './types';
import { useHostAvailabilityDetail } from './useHostAvailabilityDetail';

interface Props {
  availabilityId: string | null;
  isOpen: boolean;
  onClose: () => void;
  /** Opens the request review modal for the tapped requester (wired by the parent). */
  onSeeRequest: (request: HostPlayRequest) => void;
  /** Opens the availability edit flow. Absent for this slice — Edit is then disabled. */
  onEdit?: () => void;
}

/**
 * The host's view of one of their own player availabilities: the same identity +
 * "looking for" + availability + preferred-location sections as the public viewer
 * modal, plus an embedded card of pending play requests (each opens the review
 * modal via `onSeeRequest`) and a Cancel / Edit footer instead of "Request to play".
 */
export default function HostAvailabilityDetailModal({
  availabilityId,
  isOpen,
  onClose,
  onSeeRequest,
  onEdit,
}: Props) {
  const { player, requests } = useHostAvailabilityDetail(availabilityId);

  const footer = (
    <div className="flex gap-ds-md">
      <Button variant="secondary" size="lg" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="lg"
        iconLeft={<Pencil className="h-5 w-5" />}
        onClick={onEdit}
        disabled={!onEdit}
      >
        Edit
      </Button>
    </div>
  );

  return (
    <DetailModalShell isOpen={isOpen} onClose={onClose} footer={footer}>
      {player && (
        <>
          <div className="flex flex-col gap-ds-xl">
            <PlayerHeader player={player} />
            <PlayerLookingFor player={player} />
          </div>

          {requests.length > 0 && (
            <div className="mt-ds-xl">
              <HostRequestsCard requests={requests} onSeeRequest={onSeeRequest} />
            </div>
          )}

          <div className="my-ds-xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />

          <div className="flex flex-col gap-ds-3xl">
            {player.username && (
              <DetailSection title="Player">
                <PersonCard
                  name={player.name}
                  username={player.username}
                  imageUrl={player.imageUrl}
                  verificationLevel={player.verificationLevel}
                />
              </DetailSection>
            )}

            {player.availability.length > 0 && (
              <PlayerAvailability availability={player.availability} />
            )}

            {player.preferredLocations && player.preferredLocations.length > 0 && (
              <PlayerPreferredLocations locations={player.preferredLocations} />
            )}
          </div>
        </>
      )}
    </DetailModalShell>
  );
}
